import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "contracts";

export function useContractList(options?: {
	companyId?: string | undefined;
	status?: string | undefined;
	type?: string | undefined;
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.companyId) params.set("companyId", options.companyId);
			if (options?.status) params.set("status", options.status);
			if (options?.type) params.set("type", options.type);
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			return api.get<{ items: Contract[]; total: number }>(`/api/contracts?${params.toString()}`);
		},
	});
}

export function useContractDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () => api.get<Contract>(`/api/contracts/${id}`),
		enabled: !!id,
	});
}

export function useCreateContract() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateContractInput) => api.post<Contract>("/api/contracts", data),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useUpdateContract() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CreateContractInput> }) =>
			api.patch<Contract>(`/api/contracts/${id}`, data),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useDeleteContract() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete<void>(`/api/contracts/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

interface Contract {
	id: string;
	documentNumber: string | null;
	title: string;
	type: "sales" | "maintenance" | "rental" | "service";
	startDate: string | null;
	endDate: string | null;
	value: string | null;
	currency: string | null;
	autoRenewal: boolean;
	renewalNoticeDays: number | null;
	terms: string | null;
	notes: string | null;
	status: "draft" | "active" | "expired" | "terminated" | "renewed";
	signedAt: string | null;
	signedBy: string | null;
	companyId: string | null;
	opportunityId: string | null;
	orderId: string | null;
	assignedTo: string | null;
	teamId: string | null;
	visibility: string;
	companyName: string | null;
	createdAt: string;
	updatedAt: string;
}

interface CreateContractInput {
	title: string;
	type?: "sales" | "maintenance" | "rental" | "service";
	startDate?: string | undefined;
	endDate?: string | undefined;
	value?: number | undefined;
	currency?: string | undefined;
	autoRenewal?: boolean;
	renewalNoticeDays?: number | undefined;
	terms?: string | undefined;
	notes?: string | undefined;
	companyId?: string | undefined;
	opportunityId?: string | undefined;
	orderId?: string | undefined;
	assignedTo?: string | undefined;
}
